import { FC, MouseEventHandler, TouchEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { zIndexes } from '@core/constant';
import { useContextMediaQuery } from '@core/context';
import { EZIndexName } from '@core/type';

import { Box } from '../layout';
import { SlickSlider } from '../slider';
import { ContainerCarouselViews } from './ContainerCarouselViews';
import { CarouselProps } from './interface-carousel';

const HALF = 2;
const DEFAULT_MARGIN = 20;
const DEFAULT_HEIGHT = 80;

const Slider = styled.div`
  z-index: ${zIndexes[EZIndexName.OVER_DEFAULT]};
  text-align: center;
`;

export const Carousel: FC<CarouselProps> = ({
  totalSteps,
  currentStep,
  onPrevious,
  onNext,
  views,
  onClick,
  withArrows,
  withCarousel,
  slidesWidth,
  slidesHeight,
  overflowX = 'hidden',
}) => {
  const [startTouchX, setStartTouchX] = useState<number>(null);
  const [endTouchX, setEndTouchX] = useState<number>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const centerBlockWithStepsRef = useRef<number>(0);
  const centerBlockRef = useRef<number>(0);

  const { isPhone, mediaQuery } = useContextMediaQuery();

  useEffect(() => {
    const screenWidth = window.screen.width || 0;

    centerBlockRef.current = isPhone
      ? screenWidth / HALF - slidesWidth / HALF - DEFAULT_MARGIN
      : slidesWidth / HALF - DEFAULT_MARGIN;
    centerBlockWithStepsRef.current = currentStep * slidesWidth - centerBlockRef.current;

    if (slidesWidth && currentStep === 0) {
      carouselRef.current.style.transform = `translateX(${centerBlockRef.current}px)`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaQuery, centerBlockWithStepsRef.current, currentStep, slidesWidth]);

  const handleSwipeStart = (clientX: number) => {
    setStartTouchX(clientX);
  };

  const handleTouchStart = ({ touches }: TouchEvent) => {
    handleSwipeStart(touches[0]?.clientX);
  };

  const handleMouseDown: MouseEventHandler = ({ clientX }) => {
    handleSwipeStart(clientX);
  };

  const handleSwiping = (clientX: number) => {
    setEndTouchX(clientX);

    if (slidesWidth) {
      if (startTouchX === null || carouselRef.current === null) {
        return;
      }

      const touchDiff = clientX - startTouchX;

      if (currentStep === 0) {
        carouselRef.current.style.transform = `translateX(calc(${centerBlockRef.current}px + ${touchDiff}px))`;
      } else {
        carouselRef.current.style.transform = `translateX(calc(-${centerBlockWithStepsRef.current}px + ${touchDiff}px))`;
      }
    }
  };

  const handleTouchMove = ({ touches }: TouchEvent) => {
    handleSwiping(touches[0]?.clientX);
  };

  const handleMouseMove: MouseEventHandler = ({ clientX }) => {
    handleSwiping(clientX);
  };

  const handleSwipeEnd = () => {
    if (startTouchX === null || endTouchX === null) {
      return;
    }

    const touchDiff = startTouchX - endTouchX;

    if (touchDiff > 0) {
      handleNext();
    } else if (touchDiff < 0) {
      handlePrevious();
    }

    if (slidesWidth) {
      if (currentStep > 0 && currentStep < totalSteps) {
        carouselRef.current.style.transform = `translateX(-${centerBlockWithStepsRef.current}px)`;
      } else if (currentStep === 0) {
        carouselRef.current.style.transform = `translateX(${centerBlockRef.current}px)`;
      }
    }

    setStartTouchX(null);
    setEndTouchX(null);
  };

  const handleTouchEnd = () => {
    handleSwipeEnd();
  };

  const handleMouseUp: MouseEventHandler = () => {
    handleSwipeEnd();
  };

  const handlePrevious = () => {
    if (currentStep === 0) {
      return;
    }

    onPrevious();
  };

  const handleNext = () => {
    if (currentStep === totalSteps - 1) {
      return;
    }

    onNext();
  };

  const height = slidesHeight + DEFAULT_HEIGHT || null;

  return slidesWidth ? (
    <Box position={'relative'} height={height || '500px'}>
      <Box position={'absolute'} left={'0'} right={'0'} overflowX={overflowX}>
        <ContainerCarouselViews
          ref={carouselRef}
          style={{
            transform: `translateX(-${centerBlockWithStepsRef.current}px)`,
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {views.map((view, index) => (
            <Box key={index} margin={'0 20px'}>
              {[view]}
            </Box>
          ))}
        </ContainerCarouselViews>
      </Box>

      {(withArrows || withCarousel) && (
        <Box position={'absolute'} bottom={'0'} left={'0'} width={'100%'}>
          <SlickSlider
            totalSteps={totalSteps}
            currentStep={currentStep}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onClick={onClick}
            withArrows={withArrows}
            withCarousel={withCarousel}
          />
        </Box>
      )}
    </Box>
  ) : (
    <Box position={'relative'}>
      <Slider
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        {views.flatMap((view, index) => (currentStep === index ? [view] : []))}
      </Slider>

      {(withArrows || withCarousel) && (
        <SlickSlider
          totalSteps={totalSteps}
          currentStep={currentStep}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onClick={onClick}
          withArrows={withArrows}
          withCarousel={withCarousel}
        />
      )}
    </Box>
  );
};
